import { Injectable, OnDestroy, inject, signal } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { DocumentsPlusInBasketService } from "@app/app-modules/documents-plus/documents-plus-in-basket/documents-plus-in-basket.service";
import { DocumentsPlusAttachParms } from "@app/app-modules/documents-plus/documents-plus.model";
import { DocumentsPlusService } from "@app/app-modules/documents-plus/documents-plus.service";
import { MyReportsService } from "@app/app-modules/my-reports/my-reports.service";
import { LoaderService } from "@app/core/services/loader.service";
import { MessageService } from "@app/core/services/message.service";
import { ModalSheetService } from "@app/core/services/modal-sheet.service";
import { NotifyService } from "@app/core/services/notify.service";
import { SystemSettingsService } from "@app/core/services/system-settings.service";
import { TabService } from "@app/core/services/tab.service";
import { UserSettingsService } from "@app/core/services/user-settings.service";
import { PdfDocument } from "@app/shared/components/pdf-viewer/pdf-viewer-model";
import { PdfViewerService } from "@app/shared/components/pdf-viewer/pdf-viewer.service";
import { APPLICATIONS } from "@app/shared/constants/applications.constants";
import { PDF_URL } from "@app/shared/constants/url.constants";
import {
  ExternalWindowEvent,
  ExternalWindowEventTypes,
  ExternalWindowParms,
  ExternalWindowSource
} from "@app/shared/models/external-window-handler.model";
import { NotificationMessage, PdfMessage } from "@app/shared/models/message.model";
import { environment } from "@env/environment";
import { BehaviorSubject, map, of, Subscription, switchMap } from "rxjs";
import { printTS } from "@app/app-libs/print-ts/init";
import { I18nService } from "@app/core/services/i18n.service";
import { MaterialCssVarsService } from "@app/app-libs/material-css-vars/material-css-vars.service";
import { FileTypes } from "@app/shared/constants/file-types.constants";
import { FileDownloadService } from "@app/core/services/file-download.service";
import { BreakpointService } from "@app/core/services/breakpoint.service";
import { AppInjector } from "@app/core/functions/app-injector";

@Injectable({
  providedIn: "root"
})
export class ExternalWindowHandlerService implements OnDestroy {
  tabDocumentData = signal<PdfDocument>(undefined);
  windowDocumentData = signal<PdfDocument[]>([]);
  windowSessionCount: number = 0;

  private _windowRefs: WindowProxy[] = [];
  private _windowLoadedSub: Subscription;
  private _windowLoaded = new BehaviorSubject<boolean>(false);

  private userSettings = inject(UserSettingsService);
  private systemSettings = inject(SystemSettingsService);
  private documentsPlusService = inject(DocumentsPlusService);
  private inBasketService = inject(DocumentsPlusInBasketService);
  private myReportsService = inject(MyReportsService);
  private messageService = inject(MessageService);
  private tabService = inject(TabService);
  private pdfService = inject(PdfViewerService);
  private notifyService = inject(NotifyService);
  private loaderService = inject(LoaderService);
  private modalSheetService = inject(ModalSheetService);
  private i18nService = inject(I18nService);
  private materialCssVars = inject(MaterialCssVarsService);
  private fileDownloadService = inject(FileDownloadService);

  ngOnDestroy(): void {
    this.clearWindowRefs();
    this._windowLoadedSub?.unsubscribe();
  }

  /**
   * Handle events from ussi window
   * @param event
   */
  handleEvent(event: MessageEvent) {
    const eventData: ExternalWindowEvent = event?.data;
    if (eventData?.source !== ExternalWindowSource) {
      return;
    }

    switch (eventData.event) {
      case ExternalWindowEventTypes.WindowLoaded: {
        this._windowLoaded.next(true);
        break;
      }
      case ExternalWindowEventTypes.OpenWindow: {
        this.handleOpenWindowEvent(eventData);
        break;
      }
      case ExternalWindowEventTypes.ReturnWindow: {
        this.pdfService.openPdf(eventData?.data?.data, true);
        break;
      }
      case ExternalWindowEventTypes.CloseWindow: {
        this.handleCloseWindowEvent(eventData?.data);
        break;
      }
      case ExternalWindowEventTypes.Attach: {
        this.attach(event.source, eventData.data);
        break;
      }
      case ExternalWindowEventTypes.InBasketRead: {
        this.messageService.sendMarkAsReadInBasketSubject(eventData.data);
        break;
      }
      case ExternalWindowEventTypes.GetBase64Document: {
        this.getBase64Document(event.source, eventData.data);
        break;
      }
      case ExternalWindowEventTypes.GetDocumentsPlusDocument: {
        this.getDocumentsPlusDocument(event.source, eventData.data);
        break;
      }
      case ExternalWindowEventTypes.GetDocumentWizardDocument: {
        this.getDocumentWizardDocument(event.source, eventData.data);
        break;
      }
      case ExternalWindowEventTypes.GetMyReportsDocument: {
        this.getMyReportsDocument(event.source, eventData.data);
        break;
      }
      case ExternalWindowEventTypes.StoreTabDocument: {
        this.storeTabDocument(eventData.data);
        break;
      }
      case ExternalWindowEventTypes.StoreWindowDocument: {
        this.storeWindowDocument(event.source, eventData.data);
        break;
      }
      case ExternalWindowEventTypes.CheckDirty: {
        this.handleCheckDirtyEvent(eventData.data);
        break;
      }
      case ExternalWindowEventTypes.FormStatus: {
        this.handleFormStatus(eventData.data);
        break;
      }
      case ExternalWindowEventTypes.Loading: {
        this.toggleLoading(eventData.data);
        break;
      }
      case ExternalWindowEventTypes.Notification: {
        this.handleNotification(eventData.data);
        break;
      }
      case ExternalWindowEventTypes.Print: {
        this.printMessage(eventData.data);
        break;
      }
    }
  }

  /**
   * Open external application window
   * @param data
   * @param event
   */
  openExternalWindow(data: PdfMessage, event?: number, applicationId?: string) {
    // Update existing window if it exists and is not closed
    if (this.activeWindow?.closed === false && this.userSettings.viewerSettings.viewDocumentReplaceWindow) {
      this.postMessage(this.activeWindow, data, event, applicationId);
    } else {
      this.clearClosedWindows();

      // Check for existing document in window
      if (!this._canOpenWindow(data)) {
        return;
      }

      const windowRef = window.open(
        this.url,
        "_blank",
        `directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,top=0,left=0,height=${window.screen.height},width=${window.screen.width / 2}`
      );

      // If the window fails to open
      if (!windowRef) {
        this._openWindowErrorModal();
        return;
      }

      this._windowRefs.push(windowRef);
      this.windowSessionCount++;

      // Wait for the external window to render and we get a message telling the main application it's ready to receive.
      // When _windowLoaded is true we can send our data and then unsubscribe.
      this._windowLoadedSub = this._windowLoaded.subscribe((loaded) => {
        if (loaded) {
          this.postMessage(windowRef, data, event, applicationId);
          this._windowLoaded.next(false);
          this._windowLoadedSub?.unsubscribe();
        }
      });
    }
  }

  /**
   * Send message to pdf window/iframe and set focus
   * @param windowRef
   * @param data
   * @param event
   */
  postMessage(windowRef: WindowProxy, data: any, event?: number, applicationId?: string) {
    if (!windowRef && !this._windowRefs?.length) {
      return;
    }

    // Default to first open window
    if (!windowRef) {
      windowRef = this.activeWindow;
    }
    windowRef.postMessage(this.setWindowParms(data, event, applicationId), PDF_URL.VIEWER);

    // Don't focus pdf-viewer in background for House - assume identity
    if (this.userSettings.authService.credentials.isHouseAccount && !this.userSettings.authService.credentials.isIdentityAssumed) {
      return;
    }

    windowRef.focus();
  }

  /**
   * Check form status of open window. If the form is not pristine notify user and focus window
   * @returns
   */
  canClose() {
    if (this.tabService?.pdfTab?.form?.dirty) {
      this.notifyService.warning("APP.MSG_WARNING", "APP.MSG_FORM_DIRTY");
      this.focus(null);
      return false;
    }
    this.clearWindowRefs();
    return true;
  }

  handleOpenWindowEvent(eventData: any) {
    this.tabService.removePdfViewerTab();
    this.openExternalWindow(eventData.data.data, null, eventData.data.applicationId);
  }

  handleCloseWindowEvent(isExternalWindow: boolean) {
    if (isExternalWindow) {
      this.clearWindowRefs();
    } else {
      this.tabService.removePdfViewerTab();
    }
  }

  handleFormStatus(isDirty: boolean) {
    if (this.tabService.pdfTab.hidden()) {
      return;
    }

    if (!this.tabService?.pdfTab?.form) {
      this.tabService.pdfTab.form = new FormGroup({});
    }

    if (isDirty) {
      this.tabService.pdfTab.form.markAsDirty();
    } else {
      this.tabService.pdfTab.form.markAsPristine();
    }
  }

  handleCheckDirtyEvent(closeTab: boolean) {
    if (closeTab) {
      this.tabService.removePdfViewerTab();
    }
  }

  toggleLoading(show: boolean) {
    if (show) {
      this.loaderService.show();
    } else {
      this.loaderService.hide(true);
    }
  }

  handleNotification(data: NotificationMessage) {
    this.notifyService.openMessage(data.message, data.action);
  }

  /**
   * Generic attach method
   * @param winSource
   * @param data
   * @param sendMessage
   */
  attach(winSource: MessageEventSource, data: PdfDocument, sendMessage: boolean = true) {
    if (data?.reportsPlus) {
      data.docPlus = {
        documentTypeDescription: data.docName,
        ids: [data.docPath],
        reportsPlus: true
      } as DocumentsPlusAttachParms;
    }

    return this.inBasketService.doAttach(data.docPlus).pipe(
      switchMap((resp) => {
        if (resp) {
          if (resp?.deleteDoc) {
            this.notifyService.hideNotifications();
            return this.inBasketService.deleteDocument(data.docPlus.ids);
          }

          return of(resp);
        }
        return of(null);
      }),
      map((resp) => {
        if (resp) {
          this.messageService.sendRefreshInBasketSubject();
          this.postMessage(winSource as Window, null, ExternalWindowEventTypes.OpenNextDocument);
        }

        this.notifyService.showNotifications();
        if (sendMessage) {
          this.messageService.sendShowAttachDocumentsMessage();
        }
        return resp;
      })
    );
  }

  /**
   * Retrieve document as base64
   * @param window
   * @param documentId
   */
  getBase64Document(window: MessageEventSource, pdfMessage: PdfMessage) {
    this.fileDownloadService.downloadFileAsBase64({ file: pdfMessage.document.docPath, type: FileTypes.ADOBE }).subscribe({
      next: (resp) => {
        this.postMessage(window as Window, resp, ExternalWindowEventTypes.GetBase64Document);
      },
      error: () => {}
    });
  }

  /**
   * Retrieve DocumentsPlus document and send back to viewer
   * @param window
   * @param uniqueId
   */
  getDocumentsPlusDocument(window: MessageEventSource, pdfMessage: PdfMessage) {
    this.tabService.activeMenuOptionApplication = pdfMessage?.applicationId;
    this.documentsPlusService.viewDocument({ uniqueId: pdfMessage.docId }).subscribe({
      next: (resp) => {
        this.postMessage(window as Window, resp.url, ExternalWindowEventTypes.GetDocumentsPlusDocument);
      },
      error: () => {
        this.postMessage(window as Window, null, ExternalWindowEventTypes.GetDocumentsPlusDocument);
      }
    });
  }

  /**
   * Retrieve legacy Document wizard document and send back to viewer
   * @param window
   * @param documentId
   */
  getDocumentWizardDocument(window: MessageEventSource, pdfMessage: PdfMessage) {
    this.tabService.activeMenuOptionApplication = pdfMessage?.applicationId;

    const sendMessage = (file: any) => {
      this.postMessage(window as Window, file, ExternalWindowEventTypes.GetDocumentWizardDocument);
    };
    this.documentsPlusService.fetchAndViewDocument(pdfMessage.document, pdfMessage.documentList, true, pdfMessage?.applicationId, sendMessage);
  }

  /**
   * Retrieve MyReports document and send back to viewer
   * @param window
   * @param filePath
   */
  getMyReportsDocument(window: MessageEventSource, data: PdfMessage) {
    this.myReportsService.viewDocument(data.jobId, data.document.docPath).subscribe({
      next: (resp) => {
        this.postMessage(window as Window, resp.url, ExternalWindowEventTypes.GetMyReportsDocument);
      },
      error: () => {}
    });
  }

  /**
   * Store actively viewed document on tab viewer
   * @param data
   */
  storeTabDocument(data: PdfDocument) {
    data.windowRef = this.tabService.pdfTab.externalWindowRef;
    this.tabDocumentData.set(data);
  }

  /**
   * Store actively viewed document on external viewer
   * @param data
   */
  storeWindowDocument(window: MessageEventSource, data: PdfDocument) {
    data.windowRef = window as Window;
    this.windowDocumentData.update((x) => {
      // Replace existing record with new one
      const i = this.windowDocumentData().findIndex((x) => x.sessionNumber === data.sessionNumber);
      if (i > -1) {
        x.splice(i, 1, data);
        return x;
      }

      return [...x, data];
    });
  }

  /**
   * Loop through all windows and send dark mode update
   */
  toggleDarkModeMessage() {
    this.postMessage(this.tabService.pdfTab.externalWindowRef, this.materialCssVars.isDarkTheme, ExternalWindowEventTypes.ToggleTheme);
    this._windowRefs.some((windowRef) => {
      this.postMessage(windowRef, this.materialCssVars.isDarkTheme, ExternalWindowEventTypes.ToggleTheme);
    });
  }

  /**
   * Close all open windows and clear array
   */
  clearWindowRefs() {
    this._windowRefs.some((windowRef) => {
      this.postMessage(windowRef, null, ExternalWindowEventTypes.CloseWindow);
    });
    this._windowRefs = [];
    this.windowDocumentData.set([]);
    this.tabDocumentData.set(undefined);
    this.windowSessionCount = 0;
  }

  /**
   * Clean up arrays with closed windows
   */
  clearClosedWindows() {
    this._windowRefs = this._windowRefs.filter((x) => !x?.closed);
    this.windowDocumentData.set(this.windowDocumentData().filter((x) => !x?.windowRef?.closed));
  }

  /**
   * Set focus to window or tab
   * @param windowRef
   */
  focus(windowRef: Window) {
    if (windowRef?.opener) {
      windowRef.focus();
    } else {
      this.tabService.setFocusPdfTab();
    }
  }

  /**
   * url to print
   * @param url
   */
  printMessage(url: string) {
    this.modalSheetService.openLoadingModal("APP.LBL_LOADING").subscribe();

    fetch(url)
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);

        const close = () => {
          this.modalSheetService.closeAll();
        };

        printTS({ printable: blobUrl, type: FileTypes.ADOBE, onLoadingEnd: close, onError: close });
      });
  }

  /**
   * Build parms to send to pdf-viewer
   * @param data
   * @param event
   * @returns
   */
  setWindowParms(data: any, event: number, applicationId?: string): ExternalWindowParms {
    return {
      title: this.i18nService.translate("PDF_VIEWER.LBL_VIEWER_TITLE", {
        domain: this.systemSettings.systemSettings.name,
        sessionNumber: this.windowSessionCount
      }),
      sessionNumber: this.windowSessionCount,
      source: ExternalWindowSource,
      event: event ?? ExternalWindowEventTypes.Init,
      credentials: {
        displayName: this.userSettings.authService.credentials.displayName,
        username: this.userSettings.authService.credentials.username,
        token: this.userSettings.authService.token,
        nodeJsAuthentication: this.userSettings.authService.credentials.nodeJsAuthentication
      },
      toggleSettings: {
        showOpen: true,
        showReturn: true
      },
      serverUrl: environment.serverUrl,
      origin: window.origin,
      applicationId: applicationId ?? APPLICATIONS.CORE,
      localeSettings: {
        ...this.systemSettings.systemSettings.localeSettings,
        language: this.userSettings.language
      },
      inputSettings: this.userSettings.inputSettings,
      notificationSettings: this.userSettings.notificationSettings,
      tooltipSettings: this.userSettings.tooltipSettings,
      inbasketSettings: this.userSettings.inbasketSettings,
      themeSettings: {
        ...this.userSettings.theme,
        isDarkTheme: this.materialCssVars.isDarkTheme
      },
      viewerSettings: this.userSettings.viewerSettings,
      data: data
    };
  }

  /**
   * Build url to send data
   * @param data
   * @param event
   * @param applicationId
   * @returns
   */
  get url(): string {
    return `${PDF_URL.VIEWER}?ngsw-bypass=true`;
  }

  /**
   * Return if there are any open windows
   */
  get isOpen(): boolean {
    return this._windowRefs?.length && this.activeWindow !== undefined;
  }

  /**
   * Look at window references for at least 1 active window.
   */
  get activeWindow() {
    return this._windowRefs.find((x) => !x.closed);
  }

  /**
   * Filter stored data for windows that are NOT closed and have a document type
   */
  get activeAttachWindows() {
    return this.windowDocumentData().filter((x) => x.windowRef?.closed === false && (x?.docPlus?.documentType || x?.reportsPlus)) ?? [];
  }

  /**
   * Check if tab is active and can be attached
   */
  get activeTabWindows() {
    return !this.tabService.pdfTab.hidden() && (this.tabDocumentData()?.docPlus?.documentType || this.tabDocumentData()?.reportsPlus);
  }

  get windowRefs() {
    return this._windowRefs;
  }

  get bps() {
    return AppInjector.get(BreakpointService);
  }

  private _openWindowErrorModal() {
    const title = "ERR_WINDOW_OPEN";
    const body = "ERR_WINDOW_OPEN_MSG";
    this.modalSheetService.openWarningModal(title, body, false).subscribe();
  }

  /**
   * Check if window already exists for document path.
   * NOTE: This only checks if the window does not have multiple-documents
   */
  private _canOpenWindow(data: PdfMessage) {
    if (data?.documentList?.length) {
      return true;
    }

    const windowRef = this.windowDocumentData().find((x) => x.docPath === data?.document?.docPath)?.windowRef;
    if (windowRef?.closed === false) {
      windowRef?.focus();
      return false;
    }

    return true;
  }
}
